import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const APP_SCHEME = 'playersturf://';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.playersturf';
const APP_STORE_URL = 'https://apps.apple.com/us/app/players-turf-sports-booking/id6738467400';

const isAndroid = () => /Android/i.test(navigator.userAgent);
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

const openWithIntentOnAndroid = (code) => {
    const fallback = encodeURIComponent(PLAY_STORE_URL);
    const intentUrl = `intent://refer/${code}#Intent;scheme=playersturf;package=com.playersturf;S.browser_fallback_url=${fallback};end`;
    window.location.href = intentUrl;
};

const ReferralRedirect = () => {
    const { code } = useParams();
    const [redirected, setRedirected] = useState(false);

    const storeUrl = isIOS() ? APP_STORE_URL : PLAY_STORE_URL;
    const isDesktop = !isAndroid() && !isIOS();

    useEffect(() => {
        if (!code) return;

        if (isAndroid()) {
            // Android intent — browser_fallback_url handles store redirect automatically
            openWithIntentOnAndroid(code);
            return;
        }

        if (isIOS()) {
            // Try to open the app; fall back to App Store after 1.5s
            const start = Date.now();
            window.location.href = `${APP_SCHEME}refer/${code}`;

            const timer = setTimeout(() => {
                // Only redirect if the page is still visible (app didn't open)
                if (Date.now() - start < 2000) {
                    setRedirected(true);
                    window.location.href = APP_STORE_URL;
                }
            }, 1500);

            return () => clearTimeout(timer);
        }

        // Desktop — no auto-redirect; show both store buttons
    }, [code]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 via-orange-500 to-red-500 text-white px-4">
            <div className="text-center max-w-sm w-full">
                {/* App icon */}
                <div className="w-24 h-24 rounded-3xl overflow-hidden mx-auto mb-6 shadow-lg">
                    <img src="https://players-turf-storage.s3.ap-south-1.amazonaws.com/profile/Super_logo.jpeg" alt="Players Turf" className="w-full h-full object-cover" />
                </div>

                <h1 className="text-2xl font-bold mb-2">Your friend invited you to Players Turf!</h1>
                <p className="text-white/80 text-sm mb-6">Book sports venues near you and get rewarded.</p>

                {/* Referral code badge */}
                <div className="bg-white/20 border border-white/30 rounded-2xl px-6 py-4 mb-6 inline-block">
                    <p className="text-xs uppercase tracking-widest text-white/70 mb-1">Your Referral Code</p>
                    <p className="text-3xl font-extrabold tracking-wider">{code}</p>
                </div>

                <p className="text-white/80 text-sm mb-8">Download the app to get your reward</p>

                {/* Store buttons */}
                {isDesktop ? (
                    // Desktop — show both badges
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href={PLAY_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 bg-black/40 hover:bg-black/60 border border-white/30 rounded-xl px-5 py-3 transition"
                        >
                            <svg className="w-7 h-7 flex-shrink-0" viewBox="0 0 512 512" fill="currentColor">
                                <path d="M48 59.49v393a4.33 4.33 0 0 0 7.37 3.07L274 256 55.37 56.42A4.33 4.33 0 0 0 48 59.49zM345.8 174L89.22 32.64l-.16-.09c-4.42-2.4-8.62 3.58-5 7.06L285.19 240zM84.08 472.39c-3.64 3.48.56 9.46 5 7.06l.16-.09L345.8 338l-60.61-66zM449.38 231l-71.65-39.46L310.36 256l67.37 64.43L449.38 281c19.49-10.77 19.49-39.23 0-50z" />
                            </svg>
                            <div className="text-left">
                                <p className="text-xs text-white/70">Get it on</p>
                                <p className="text-sm font-semibold">Google Play</p>
                            </div>
                        </a>
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 bg-black/40 hover:bg-black/60 border border-white/30 rounded-xl px-5 py-3 transition"
                        >
                            <svg className="w-7 h-7 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11" />
                            </svg>
                            <div className="text-left">
                                <p className="text-xs text-white/70">Download on the</p>
                                <p className="text-sm font-semibold">App Store</p>
                            </div>
                        </a>
                    </div>
                ) : (
                    // Mobile — single button for detected platform
                    <a
                        href={storeUrl}
                        className="inline-block bg-white text-purple-700 font-bold px-8 py-3 rounded-full shadow-lg hover:bg-white/90 transition text-sm"
                    >
                        {isIOS() ? 'Download on the App Store' : 'Get it on Google Play'}
                    </a>
                )}

                {/* Manual fallback for mobile */}
                {!isDesktop && (
                    <p className="mt-6 text-xs text-white/60">
                        App not opening?{' '}
                        <a href={storeUrl} className="underline text-white/80">
                            Tap here to go to the store
                        </a>
                    </p>
                )}
            </div>
        </div>
    );
};

export default ReferralRedirect;
